/* global BigInt */

export const TRANSACTION_PREHEAT_STORAGE_KEY = 'maiko_transaction_preheat_settings';

export const TRANSACTION_PREHEAT_MODE_DYNAMIC = 'dynamic';
export const TRANSACTION_PREHEAT_MODE_PREPARED = 'prepared';

export const TRANSACTION_GAS_STRATEGY_ESTIMATE = 'estimate';
export const TRANSACTION_GAS_STRATEGY_FIXED = 'fixed';

export const TRANSACTION_PREBUILD_LEVEL_NONE = 'none';
export const TRANSACTION_PREBUILD_LEVEL_CALLDATA = 'calldata';
export const TRANSACTION_PREBUILD_LEVEL_RAW = 'raw';

export const DEFAULT_FIXED_GAS_LIMIT = '1000000';

const getStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage;
    }
  } catch (error) {
    return null;
  }
  return null;
};

const normalizePositiveIntegerString = (value, fallback = DEFAULT_FIXED_GAS_LIMIT) => {
  const text = String(value ?? '').trim();
  if (!/^[1-9]\d*$/.test(text)) return fallback;
  return text;
};

export const normalizeTransactionPreheatMode = (value) => (
  value === TRANSACTION_PREHEAT_MODE_PREPARED
    ? TRANSACTION_PREHEAT_MODE_PREPARED
    : TRANSACTION_PREHEAT_MODE_DYNAMIC
);

export const normalizeTransactionGasStrategy = (value) => (
  value === TRANSACTION_GAS_STRATEGY_FIXED
    ? TRANSACTION_GAS_STRATEGY_FIXED
    : TRANSACTION_GAS_STRATEGY_ESTIMATE
);

export const normalizeTransactionPrebuildLevel = (value) => {
  if (value === TRANSACTION_PREBUILD_LEVEL_RAW) return TRANSACTION_PREBUILD_LEVEL_RAW;
  return TRANSACTION_PREBUILD_LEVEL_NONE;
};

export const normalizeTransactionPreheatSettings = (settings = {}) => ({
  preheatMode: normalizeTransactionPreheatMode(settings.preheatMode),
  gasStrategy: normalizeTransactionGasStrategy(settings.gasStrategy),
  prebuildLevel: normalizeTransactionPrebuildLevel(settings.prebuildLevel),
  fixedGasLimit: normalizePositiveIntegerString(settings.fixedGasLimit),
});

export const readTransactionPreheatSettings = (storage = getStorage()) => {
  if (!storage) return normalizeTransactionPreheatSettings();
  try {
    const raw = storage.getItem(TRANSACTION_PREHEAT_STORAGE_KEY);
    return normalizeTransactionPreheatSettings(raw ? JSON.parse(raw) : {});
  } catch (error) {
    return normalizeTransactionPreheatSettings();
  }
};

export const writeTransactionPreheatSettings = (settings, storage = getStorage()) => {
  const normalized = normalizeTransactionPreheatSettings(settings);
  if (storage) {
    try {
      storage.setItem(TRANSACTION_PREHEAT_STORAGE_KEY, JSON.stringify(normalized));
    } catch (error) {
      // Ignore storage failures; transaction code still uses the normalized settings.
    }
  }
  return normalized;
};

export const isPreparedTransactionMode = (settings) => (
  normalizeTransactionPreheatSettings(settings).preheatMode === TRANSACTION_PREHEAT_MODE_PREPARED
);

export const isFixedGasStrategy = (settings) => (
  normalizeTransactionPreheatSettings(settings).gasStrategy === TRANSACTION_GAS_STRATEGY_FIXED
);

export const isRawPreSignEnabled = (settings) => {
  const normalized = normalizeTransactionPreheatSettings(settings);
  return normalized.preheatMode === TRANSACTION_PREHEAT_MODE_PREPARED
    && normalized.gasStrategy === TRANSACTION_GAS_STRATEGY_FIXED
    && normalized.prebuildLevel === TRANSACTION_PREBUILD_LEVEL_RAW;
};

export const isCalldataPrebuildEnabled = (settings) => {
  const normalized = normalizeTransactionPreheatSettings(settings);
  return normalized.preheatMode === TRANSACTION_PREHEAT_MODE_PREPARED
    && normalized.prebuildLevel === TRANSACTION_PREBUILD_LEVEL_RAW;
};

export const applyTransactionPreheatOverrides = (transaction = {}, settings = {}) => {
  const normalized = normalizeTransactionPreheatSettings(settings);
  const next = { ...transaction };

  if (
    normalized.gasStrategy === TRANSACTION_GAS_STRATEGY_FIXED
  ) {
    delete next.gas;
    next.gasLimit = BigInt(normalized.fixedGasLimit);
  }

  return next;
};

const hasFeeFields = (transaction = {}) => (
  transaction.gasPrice !== undefined
  || transaction.maxFeePerGas !== undefined
  || transaction.maxPriorityFeePerGas !== undefined
);

export const applyPreparedFeeOverrides = (transaction = {}, settings = {}, feeData = null) => {
  const normalized = normalizeTransactionPreheatSettings(settings);
  const next = { ...transaction };
  if (normalized.preheatMode !== TRANSACTION_PREHEAT_MODE_PREPARED || hasFeeFields(next) || !feeData) {
    return next;
  }

  if (feeData.gasPrice !== undefined && feeData.gasPrice !== null) {
    next.gasPrice = feeData.gasPrice;
  } else {
    if (feeData.maxFeePerGas !== undefined && feeData.maxFeePerGas !== null) {
      next.maxFeePerGas = feeData.maxFeePerGas;
    }
    if (feeData.maxPriorityFeePerGas !== undefined && feeData.maxPriorityFeePerGas !== null) {
      next.maxPriorityFeePerGas = feeData.maxPriorityFeePerGas;
    }
  }

  return next;
};

const normalizeNonceAddress = (address) => String(address || '').trim().toLowerCase();

const normalizeNonceScope = (scope) => {
  if (scope && typeof scope === 'object') {
    return {
      chainId: String(scope.chainId ?? '').trim(),
      rpcUrl: String(scope.rpcUrl ?? '').trim(),
      address: normalizeNonceAddress(scope.address),
    };
  }
  return {
    chainId: '',
    rpcUrl: '',
    address: normalizeNonceAddress(scope),
  };
};

const nonceScopeKey = (scope) => {
  const normalized = normalizeNonceScope(scope);
  if (!normalized.address) return '';
  return `${normalized.chainId}|${normalized.rpcUrl}|${normalized.address}`;
};

class TransactionPreheatService {
  constructor() {
    this.settings = readTransactionPreheatSettings();
    this.nonces = new Map();
    this.feeData = null;
  }

  setSettings(settings) {
    this.settings = normalizeTransactionPreheatSettings(settings);
    return this.settings;
  }

  getSettings() {
    this.settings = readTransactionPreheatSettings();
    return this.settings;
  }

  clearNonces() {
    this.nonces.clear();
  }

  clearFeeData() {
    this.feeData = null;
  }

  primeFeeData(feeData = {}) {
    this.feeData = {
      gasPrice: feeData.gasPrice ?? null,
      maxFeePerGas: feeData.maxFeePerGas ?? null,
      maxPriorityFeePerGas: feeData.maxPriorityFeePerGas ?? null,
      updatedAt: Date.now(),
    };
  }

  getFeeData() {
    return this.feeData;
  }

  primeNonce(scope, nonce) {
    const key = nonceScopeKey(scope);
    if (!key || nonce === undefined || nonce === null) return;
    const nextNonce = BigInt(nonce);
    const current = this.nonces.get(key);
    if (!current || current.invalidated) {
      this.nonces.set(key, { next: nextNonce, reservations: new Map(), invalidated: false });
      return;
    }
    if (current.next < nextNonce) current.next = nextNonce;
  }

  reserveNonce(scope) {
    const range = this.reserveNonceRange(scope, 1);
    return range.length === 1 ? range[0] : null;
  }

  reserveSpecificNonce(scope, nonce) {
    const key = nonceScopeKey(scope);
    const state = key ? this.nonces.get(key) : null;
    if (!state || state.invalidated || nonce === null || nonce === undefined) return false;
    const expected = BigInt(nonce);
    if (expected !== state.next) return false;
    state.reservations.set(expected.toString(), 'reserved');
    state.next = expected + 1n;
    return true;
  }

  reserveNonceRange(scope, count = 1) {
    const key = nonceScopeKey(scope);
    const size = Math.max(0, Number.parseInt(count, 10) || 0);
    const state = key ? this.nonces.get(key) : null;
    if (!state || state.invalidated || size === 0) return [];
    const start = state.next;
    const reserved = Array.from({ length: size }, (_, index) => start + BigInt(index));
    reserved.forEach((nonce) => state.reservations.set(nonce.toString(), 'reserved'));
    state.next += BigInt(size);
    return reserved;
  }

  commitNonce(scope, nonce) {
    const key = nonceScopeKey(scope);
    const state = key ? this.nonces.get(key) : null;
    if (!state || state.invalidated || nonce === null || nonce === undefined) return false;
    const nonceKey = BigInt(nonce).toString();
    if (state.reservations.get(nonceKey) !== 'reserved') return false;
    state.reservations.set(nonceKey, 'submitted');
    return true;
  }

  releaseNonce(scope, nonce) {
    const key = nonceScopeKey(scope);
    const state = key ? this.nonces.get(key) : null;
    if (!key || nonce === null || nonce === undefined || !this.nonces.has(key)) return false;
    const reserved = BigInt(nonce);
    if (state.invalidated || state.reservations.get(reserved.toString()) !== 'reserved') return false;
    if (state.next !== reserved + 1n) return false;
    state.reservations.delete(reserved.toString());
    state.next = reserved;
    return true;
  }

  invalidateNonce(scope, nonce) {
    const key = nonceScopeKey(scope);
    const state = key ? this.nonces.get(key) : null;
    if (!state) return false;
    if (nonce !== undefined && nonce !== null && !state.reservations.has(BigInt(nonce).toString())) {
      return false;
    }
    state.invalidated = true;
    return true;
  }

  reconcileNonce(scope, pendingNonce) {
    const key = nonceScopeKey(scope);
    if (!key || pendingNonce === undefined || pendingNonce === null) return false;
    this.nonces.set(key, {
      next: BigInt(pendingNonce),
      reservations: new Map(),
      invalidated: false,
    });
    return true;
  }

  async preheatWalletNonces({ wallets = [], provider } = {}) {
    if (!provider || typeof provider.getTransactionCount !== 'function') return [];
    const uniqueWallets = Array.from(
      new Map(
        wallets
          .filter((wallet) => wallet?.address && wallet.enabled !== false)
          .map((wallet) => [normalizeNonceAddress(wallet.address), wallet])
      ).values()
    );

    const results = await Promise.allSettled(uniqueWallets.map(async (wallet) => {
      const nonce = await provider.getTransactionCount(wallet.address, 'pending');
      const scope = provider.chainId || provider.rpcUrl
        ? { chainId: provider.chainId, rpcUrl: provider.rpcUrl, address: wallet.address }
        : wallet.address;
      this.primeNonce(scope, nonce);
      return { address: wallet.address, nonce: BigInt(nonce) };
    }));

    return results
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);
  }
}

export const transactionPreheatService = new TransactionPreheatService();
